/**
 * Stage 5E.1 — Ordly platform theme bootstrap + live refresh.
 *
 * Single source of truth for the 17 --ordly-* CSS variables on the
 * document root. The endpoint is public, served by every surface
 * (anon, authed Company, authed Accountant, admin).
 *
 * Behaviour:
 *   - On import: synchronously seed Classic on :root so first paint
 *     uses Ordly Classic values even before the network call returns.
 *     If a server-injected <style id="ordly-theme-bootstrap"> block
 *     exists in <head>, the browser has already applied those values
 *     and our seed is a no-op overlap with the same values.
 *   - Then fetch /api/ordly-theme. If the active theme differs (by
 *     version_marker), apply the new values + cache the marker for
 *     next conditional refetch.
 *   - On any failure (5xx, network, JSON, missing field): keep the
 *     current values. No FOUC, no crash, no error UI.
 *
 * What is NOT done here:
 *   - Build-time flag. There isn't one in Stage 5E.1 — the only
 *     toggle is the backend ORDLY_PLATFORM_THEME_ENABLED. When it is
 *     OFF the public endpoint returns Classic regardless of DB
 *     state, so the frontend code path is identical.
 *   - JS-level branding decisions. Components consume the variables
 *     via CSS; they never read window.* or import this module.
 */

const TOKEN_KEYS = [
    "primary", "primary_hover", "primary_active", "on_primary",
    "accent", "accent_subtle", "on_accent", "accent_bright",
    "capture", "on_capture", "capture_subtle", "capture_hover", "capture_active",
    "background", "surface", "surface_subtle",
    "text", "text_muted",
    "border", "border_strong", "focus_ring",
    "state_done",
];

// Hard-coded Classic — mirrors backend/ordly/ordly_theme_router.py
// CLASSIC_TOKENS exactly. Used as the synchronous seed.
export const ORDLY_CLASSIC = Object.freeze({
    primary:         "#07152e",
    primary_hover:   "#0f6e51",
    primary_active:  "#0a5239",
    on_primary:      "#ffffff",
    accent:          "#127a58",
    accent_subtle:   "#e6f4ed",
    on_accent:       "#ffffff",
    accent_bright:   "#1aa17a",
    capture:         "#127a58",
    on_capture:      "#ffffff",
    capture_subtle:  "#c5e6d8",
    capture_hover:   "#0f6e51",
    capture_active:  "#0a5239",
    background:      "#f7f8f5",
    surface:         "#ffffff",
    surface_subtle:  "#f0f2f5",
    text:            "#07152e",
    text_muted:      "#5c6a80",
    border:          "#e6e9ee",
    border_strong:   "#94a3b8",
    focus_ring:      "#2a9070",
    state_done:      "#2e7d5b",
});

function _apply(tokens) {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    for (const k of TOKEN_KEYS) {
        const v = tokens[k];
        if (typeof v === "string" && v.length > 0) {
            root.style.setProperty(`--ordly-${k.replace(/_/g, "-")}`, v);
        }
    }
}

let _currentMarker = null;

export function applyClassicSync() {
    _apply(ORDLY_CLASSIC);
}

export async function refreshOrdlyTheme() {
    if (typeof window === "undefined") return;
    try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const headers = {};
        if (_currentMarker) headers["If-None-Match"] = `"${_currentMarker}"`;
        const resp = await fetch(`${base}/api/ordly-theme`, {
            method: "GET",
            credentials: "omit",
            headers,
        });
        if (resp.status === 304) return;
        if (!resp.ok) return;
        const body = await resp.json();
        if (!body || !body.tokens) return;
        _apply(body.tokens);
        if (body.version_marker) _currentMarker = body.version_marker;
    } catch (_e) {
        // Swallow — keep current values. No FOUC, no error UI.
    }
}

// Synchronously seed at module-load time so the very first paint
// uses Classic values even if the network call is slow or fails.
applyClassicSync();
